import {
  Building2,
  CalendarClock,
  MapPin,
  MessageSquareText,
  SlidersHorizontal,
  Palette,
  Plug,
  CreditCard,
  ScrollText,
  UserRound,
  Users,
  type LucideIcon,
} from 'lucide-react'
import { Link } from 'react-router'

import { useCapabilities } from '@/auth'
import { hasAnyPermission } from '@/auth/capabilities'
import { PageHeader, Panel } from '@/design'

interface HubCard {
  readonly to: string
  readonly title: string
  readonly description: string
  readonly icon: LucideIcon
  /** Any of these reveals the card. */
  readonly anyOf: readonly string[]
}

/** Cards are gated individually (FRONTEND_PLAN §8 #3); feature phases append here. */
export const SETTINGS_CARDS: readonly HubCard[] = [
  {
    to: '/settings/organization',
    title: 'Organisation',
    description: 'Name, default timezone and calendar mode.',
    icon: Building2,
    anyOf: ['organizations:read'],
  },
  {
    to: '/settings/locations',
    title: 'Locations',
    description: 'Sites, opening hours and quiet hours.',
    icon: MapPin,
    anyOf: ['locations:read'],
  },
  {
    to: '/settings/team',
    title: 'Team',
    description: 'Invite staff, set roles and locations.',
    icon: Users,
    anyOf: ['users:read'],
  },
  {
    to: '/settings/templates',
    title: 'Message templates',
    description: 'The words the system sends, versioned and approved.',
    icon: MessageSquareText,
    anyOf: ['message-templates:read'],
  },
  {
    to: '/settings/rules',
    title: 'Automation rules',
    description: 'Batch sizes, hold lengths, confirmation and due-back timing.',
    icon: SlidersHorizontal,
    anyOf: ['workflow-rules:read'],
  },
  {
    to: '/settings/branding',
    title: 'Branding',
    description: 'Identity, colours, sender names and custom domain.',
    icon: Palette,
    anyOf: ['branding:read'],
  },
  {
    to: '/settings/integrations',
    title: 'Integrations',
    description: 'External calendar connections and their health.',
    icon: Plug,
    anyOf: ['integrations:read'],
  },
  {
    to: '/settings/billing',
    title: 'Billing',
    description: 'Plan, usage against limits, and billing actions.',
    icon: CreditCard,
    anyOf: ['billing:read'],
  },
  {
    to: '/settings/audit-log',
    title: 'Audit log',
    description: 'Every recorded change, with before and after.',
    icon: ScrollText,
    anyOf: ['audit-log:read'],
  },
  {
    to: '/settings/services',
    title: 'Services & resources',
    description: 'What you offer, durations and rooms.',
    icon: ScrollText,
    anyOf: ['services:read', 'resources:read'],
  },
  {
    to: '/settings/providers',
    title: 'Providers',
    description: 'Practitioners, weekly schedules and time off.',
    icon: CalendarClock,
    anyOf: ['providers:read'],
  },
  {
    to: '/settings/profile',
    title: 'Profile',
    description: 'Your account and password.',
    icon: UserRound,
    anyOf: [],
  },
]

export function visibleSettingsCards(
  capabilities: ReturnType<typeof useCapabilities>,
): readonly HubCard[] {
  return SETTINGS_CARDS.filter(
    (card) => card.anyOf.length === 0 || hasAnyPermission(capabilities, card.anyOf),
  )
}

export default function SettingsHubPage() {
  const capabilities = useCapabilities()
  const cards = visibleSettingsCards(capabilities)
  return (
    <div>
      <PageHeader
        title="Settings"
        description="Configure the workspace. You only see the areas your permissions cover."
      />
      <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {cards.map((card) => (
          <li key={card.to}>
            <Panel className="h-full transition-colors hover:border-brand">
              <Link to={card.to} className="flex h-full gap-3 p-4">
                <span className="flex size-10 shrink-0 items-center justify-center rounded-md bg-paper text-brand [&>svg]:size-5">
                  <card.icon aria-hidden />
                </span>
                <span className="min-w-0">
                  <span className="block text-[14px] font-semibold text-ink">{card.title}</span>
                  <span className="mt-0.5 block text-[12.5px] text-muted">{card.description}</span>
                </span>
              </Link>
            </Panel>
          </li>
        ))}
      </ul>
    </div>
  )
}
